import { z } from "zod";

import type { FormBuilderData } from "@/features/forms/api/formBuilderApi";

const optionalUuid = z.string().uuid().optional().catch(undefined);

export const formBuilderSearchSchema = z
    .object({
        pageId: optionalUuid,
        inputId: optionalUuid,
        blockId: optionalUuid,
    })
    .transform(({ pageId, inputId, blockId }) => ({
        ...(pageId ? { pageId } : {}),
        ...(inputId ? { inputId } : {}),
        ...(blockId ? { blockId } : {}),
    }));

export type FormBuilderSearch = z.infer<typeof formBuilderSearchSchema>;

type BuilderPage = FormBuilderData["pages"][number];
type BuilderInput = BuilderPage["inputs"][number];
type BuilderBlock = BuilderPage["blocks"][number];

export interface FormBuilderSelection {
    page: BuilderPage | null;
    input: BuilderInput | null;
    block: BuilderBlock | null;
    search: FormBuilderSearch;
}

export function resolveFormBuilderSelection(
    form: Pick<FormBuilderData, "pages">,
    search: FormBuilderSearch
): FormBuilderSelection {
    const pages = [...form.pages].sort((a, b) => a.page_index - b.page_index);
    const requestedPage = pages.find((page) => page.page_id === search.pageId);

    if (!(search.inputId && search.blockId)) {
        if (search.inputId) {
            const page = pages.find((candidate) => candidate.inputs.some((input) => input.input_id === search.inputId));
            const input = page?.inputs.find((candidate) => candidate.input_id === search.inputId);
            if (page && input)
                return { page, input, block: null, search: { pageId: page.page_id, inputId: input.input_id } };
        }
        if (search.blockId) {
            const page = pages.find((candidate) => candidate.blocks.some((block) => block.block_id === search.blockId));
            const block = page?.blocks.find((candidate) => candidate.block_id === search.blockId);
            if (page && block)
                return { page, input: null, block, search: { pageId: page.page_id, blockId: block.block_id } };
        }
    }

    const page = requestedPage ?? pages[0] ?? null;
    return { page, input: null, block: null, search: page ? { pageId: page.page_id } : {} };
}
